
import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { 
  getLoggedInParticipant, 
  logoutParticipant
} from '../api/participantAuth';
import { useParticipants } from '../hooks/useParticipants';
import PersonalInfoTab from '../components/PersonalInfoTab';
import HandlerTab from '../components/HandlerTab';
import BreederTab from '../components/BreederTab';
import AdminTab from '../components/AdminTab';
import EditControls from '../components/EditControls';

export default function ModernDashboard() {
  const [tab, setTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const history = useHistory();
  const { participants: allParticipants, loadParticipants } = useParticipants();
  const [participant, setParticipant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  const hasRole = (roleToCheck) => {
    if (!participant) return false;
    if (Array.isArray(participant.role)) {
      return participant.role.some(r => r.toLowerCase() === roleToCheck.toLowerCase());
    }
    return participant.role?.toLowerCase() === roleToCheck.toLowerCase();
  };

  const getAvailableTabs = () => {
    const tabs = [{ label: 'Personal Info', icon: '👤', index: 0 }];
    
    if (hasRole('handler')) {
      tabs.push({ label: 'Handler Settings', icon: '🏁', index: 1, tooltip: 'Manage all handler-specific settings and information here.' });
    }
    if (hasRole('breeder')) {
      tabs.push({ label: 'Breeder Settings', icon: '🕊️', index: 2, tooltip: 'Manage all breeder-specific settings and information here.' });
    }
    if (hasRole('admin')) {
      tabs.push({ label: 'Admin', icon: '🛡️', index: 3 });
    }
    
    return tabs;
  };

  const availableTabs = getAvailableTabs();

  useEffect(() => {
    const authData = getLoggedInParticipant();

    if (!authData || !authData.participantId) {
      history.push('/race/login');
    } else {
      loadParticipantData(authData.participantId);
    }
  }, [history]);

  // Reset tab to 0 if current tab is not available for this participant
  useEffect(() => {
    if (participant) {
      const availableTabIndices = getAvailableTabs().map(t => t.index);
      if (!availableTabIndices.includes(tab)) {
        setTab(0); // Reset to Personal Info tab
      }
    }
  }, [participant, tab]);

  const loadParticipantData = async (participantId) => {
    try {
      setLoading(true);
      // Force reload participants and get the fresh list directly
      const freshParticipants = await loadParticipants(true);
      
      const current = freshParticipants.find(p => p.id === participantId);
      
      if (current) {
        setParticipant(current);
      } else {
        console.error('Participant not found for ID:', participantId);
        setMessage('Unable to load your profile. Please try logging in again.');
        setTimeout(() => {
          history.push('/race/participants');
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to load participant data:', error);
      setMessage('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleParticipantUpdate = async () => {
    const authData = getLoggedInParticipant();
    if (authData?.participantId) {
      await loadParticipantData(authData.participantId);
    }
  };

  const handleLogout = () => {
    logoutParticipant();
    history.push('/race/participants');
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      // Check which tab is currently active based on the actual tab index
      // Let each tab handle its own saving state and validation
      if (tab === 0 && window.personalInfoTabSave) {
        await window.personalInfoTabSave();
      } else if (tab === 1 && hasRole('handler') && window.handlerTabSave) {
        await window.handlerTabSave();
      } else if (tab === 2 && hasRole('breeder') && window.breederTabSave) {
        await window.breederTabSave();
      } else if (tab === 3 && hasRole('admin') && window.adminTabSave) {
        await window.adminTabSave();
      }
    } catch (error) {
      console.error('Error in handleSave:', error);
      // Individual tabs handle their own saving state cleanup
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setMessage('');
  };

  if (loading) {
    return (
      <div className="container py-5">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!participant) {
    return (
      <div className="container py-5">
        <div className="alert alert-danger">
          Participant data not found. Please try logging in again.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-0 py-3 py-md-5">
      <div className="container px-0 px-md-3">
        <div className="card shadow mb-4 mx-0 mx-md-auto" style={{ borderRadius: '0', borderLeft: 'none', borderRight: 'none' }}>
        <div className="card-header bg-white">
          <div>Welcome to Member Dashboard <b><i className="bi bi-house-door-fill">{participant.fullName}</i></b>!</div>
        </div>
        <div className="card-body px-2 px-md-3">
          {message && (
            <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-info'} mb-3 mx-2 mx-md-0`}>
              {message}
            </div>
          )}

          {/* Tab Navigation - Similar to Quick Actions */}
          <div className="card shadow-sm mb-4 mx-2 mx-md-0" style={{ borderRadius: '0.375rem' }}>
            <div className="card-header bg-white py-3">
              <h5 className="mb-0" style={{ color: '#0b3d91' }}>
                <i className="bi bi-grid-3x3-gap me-2"></i>
                Dashboard Navigation
              </h5>
            </div>
            <div className="card-body">
              <div className="d-flex flex-column flex-md-row gap-3">
                {availableTabs.map((tabObj) => (
                  <button
                    key={tabObj.index}
                    className={`btn w-100 w-md-auto ${
                      tab === tabObj.index 
                        ? 'btn-primary' 
                        : 'btn-outline-primary'
                    }`}
                    style={{ minHeight: '44px' }}
                    onClick={() => setTab(tabObj.index)}
                    title={tabObj.tooltip || ''}
                  >
                    <span className="me-2" style={{ fontSize: '18px' }}>{tabObj.icon}</span>
                    {tabObj.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
  {/* Edit Controls - Visible across all tabs */}
            <div className="ms-4">
                <EditControls
                  isEditing={isEditing}
                  saving={saving}
                  onEdit={handleEdit}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
            </div>
         
          <div className="p-2 p-md-3 bg-light rounded shadow-sm mx-2 mx-md-0">
            {tab === 0 && (
              <PersonalInfoTab
                participant={participant}
                onMessage={setMessage}
                onParticipantUpdate={handleParticipantUpdate}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                saving={saving}
                setSaving={setSaving}
                onTabSwitch={setTab}
              />
            )}
            {tab === 1 && hasRole('handler') && (
              <HandlerTab
                participant={participant}
                onMessage={setMessage}
                onParticipantUpdate={handleParticipantUpdate}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                saving={saving}
                setSaving={setSaving}
              />
            )}
            {tab === 2 && hasRole('breeder') && (
              <BreederTab
                participant={participant}
                onMessage={setMessage}
                onParticipantUpdate={handleParticipantUpdate}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                saving={saving}
                setSaving={setSaving}
              />
            )}
            {tab === 3 && hasRole('admin') && (
              <AdminTab
                participant={participant}
                onMessage={setMessage}
                onParticipantUpdate={handleParticipantUpdate}
                isEditing={isEditing}
                onEditingChange={setIsEditing}
                saving={saving}
                setSaving={setSaving}
              />
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="card shadow-sm mx-0 mx-md-auto" style={{ borderRadius: '0', borderLeft: 'none', borderRight: 'none' }}>
        <div className="card-header bg-white py-3">
          <h5 className="mb-0" style={{ color: '#0b3d91' }}>
            <i className="bi bi-lightning me-2"></i>
            Quick Actions
          </h5>
        </div>
        <div className="card-body px-2 px-md-3">
          <div className="d-flex flex-column flex-md-row gap-3">
            <button 
              className="btn btn-outline-primary w-100 w-md-auto"
              style={{ minHeight: '44px' }}
              onClick={() => history.push('/race/participants')}
            >
              <i className="bi bi-arrow-left me-2"></i>
              Back to Participants List
            </button>
            <button 
              className="btn btn-outline-primary w-100 w-md-auto"
              style={{ minHeight: '44px' }}
              onClick={() => history.push('/if-convention-2026')}
            >
              <i className="bi bi-info-circle me-2"></i>
              Race Details
            </button>
            <button 
              className="btn btn-outline-secondary w-100 w-md-auto"
              style={{ minHeight: '44px' }}
              onClick={handleLogout}
            >
              <i className="bi bi-box-arrow-right me-2"></i>
              Logout
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
}
